import { useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, Link, Pagination, TextField, Tooltip, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import BoltIcon from "@mui/icons-material/Bolt";
import dayjs from "dayjs";
import { colors } from "../../../../theme";
import { vulnSeverityTier, vulnSeverityRank, severityStyleForTier, severityIconForTier, vulnAckKey, bestCvss, bestCvssVersion, exposureScoreFor, SEVERITY_TIER_ORDER } from "./assetUtils";

const nvdUrl = (cveId) => (cveId ? `https://nvd.nist.gov/vuln/detail/${cveId}` : null);

// Network-exposure badge for one asset — same score used to tie-break CVEs of
// equal severity wherever they're listed (see exposureScoreFor in assetUtils.js).
// Meant for VulnerabilityList's `renderMeta`, so it sits next to each CVE row.
export const ExposureChip = ({ row, exposureByIp }) => {
  const exp = exposureByIp?.[row?.ip];
  const details = [
    typeof exp?.connCount === "number" ? `${exp.connCount} connection${exp.connCount === 1 ? "" : "s"}` : null,
    exp?.hasExternalConnections ? "reaches external IPs" : null,
    row?.services?.length ? `${row.services.length} exposed service${row.services.length === 1 ? "" : "s"}` : null,
  ].filter(Boolean);
  return (
    <Tooltip title={details.length > 0 ? details.join(" · ") : "No recent connection data for this asset"}>
      <Chip
        label={`Exposure ${exposureScoreFor(row, exposureByIp)}`}
        size="small"
        variant="outlined"
        sx={{ fontSize: 9.5, height: 18, fontFamily: "monospace", borderColor: colors.grey[700], color: colors.grey[300] }}
      />
    </Tooltip>
  );
};

// Acknowledge/reopen block — only rendered when the parent VulnerabilityList was given
// ack-tracking props (assetId + ackMap); otherwise the row behaves exactly as before.
const AckSection = ({ ack, onAcknowledge, onReopen }) => {
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (ack) {
    return (
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, p: 1, borderRadius: 1, backgroundColor: "#4cceac0d", border: "1px solid #4cceac33" }}>
        <CheckCircleIcon sx={{ color: "#4cceac", fontSize: 18, flexShrink: 0, mt: 0.1 }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#4cceac" }}>
            Acknowledged{ack.acknowledged_by ? ` by ${ack.acknowledged_by}` : ""}
            {ack.acknowledged_at && ` · ${dayjs(ack.acknowledged_at?.$date ?? ack.acknowledged_at).format("YYYY-MM-DD HH:mm")}`}
          </Typography>
          {ack.note && <Typography sx={{ fontSize: 12, color: colors.grey[200], mt: 0.3 }}>{ack.note}</Typography>}
        </Box>
        <Button size="small" startIcon={<RestartAltIcon />} onClick={(e) => { e.stopPropagation(); onReopen(); }}>
          Reopen
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }} onClick={(e) => e.stopPropagation()}>
      <TextField
        size="small"
        fullWidth
        placeholder="Remediation note (what was done to address this CVE)…"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      <Button
        size="small"
        variant="contained"
        disableElevation
        disabled={!note.trim() || submitting}
        onClick={async () => {
          setSubmitting(true);
          await onAcknowledge(note.trim());
          setSubmitting(false);
          setNote("");
        }}
      >
        Acknowledge
      </Button>
    </Box>
  );
};

const VulnerabilityRow = ({ vuln, origin, meta, expanded, onToggle, ack, onAcknowledge, onReopen }) => {
  const tier = vulnSeverityTier(vuln);
  const c = severityStyleForTier(tier);
  const Icon = severityIconForTier(tier);
  const ackEnabled = Boolean(onAcknowledge);
  const exploits = vuln.exploits || [];
  const cvss = bestCvss(vuln);
  const cvssVersion = bestCvssVersion(vuln);

  return (
    // flexShrink: 0 matters here specifically because of `overflow: hidden`
    // below: per the flexbox spec, overflow other than `visible` resets a
    // flex item's automatic minimum size to 0 (instead of content-based),
    // which lets the flat maxHeight list above squash every row down to fit
    // its fixed height instead of scrolling — this row used to sit one level
    // deeper, inside a per-tier block div with no fixed height of its own,
    // which happened to insulate it from that shrink; the flat list removed
    // that insulation.
    <Box sx={{ borderRadius: 1, border: `1px solid ${colors.grey[800]}`, overflow: "hidden", flexShrink: 0 }}>
      <Box
        onClick={onToggle}
        sx={{
          display: "flex", alignItems: "center", gap: 0.8, flexWrap: "wrap",
          p: 1, cursor: "pointer", backgroundColor: "#222",
          "&:hover": { backgroundColor: "#262626" },
        }}
      >
        {Icon && <Icon sx={{ color: c.color, fontSize: 18, flexShrink: 0 }} />}
        {vuln.cve_id ? (
          <Link
            href={nvdUrl(vuln.cve_id)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            sx={{ fontFamily: "monospace", fontWeight: 700, fontSize: 12.5, color: "#7de8ed" }}
          >
            {vuln.cve_id}
          </Link>
        ) : (
          <Typography sx={{ fontFamily: "monospace", fontWeight: 700, fontSize: 12.5, color: colors.grey[100] }}>
            Unknown CVE
          </Typography>
        )}
        <Chip label={c.label} size="small" sx={{ bgcolor: c.bg, color: c.color, fontSize: 10, height: 18, fontWeight: 700 }} />
        {typeof cvss === "number" && (
          <Tooltip title={`CVSS ${cvssVersion} base score`}>
            <Typography sx={{ fontSize: 10.5, fontFamily: "monospace", fontWeight: 700, color: colors.grey[400] }}>
              CVSS {cvssVersion}: {cvss}
            </Typography>
          </Tooltip>
        )}
        {origin && (
          <Chip label={origin} size="small" variant="outlined" sx={{ fontSize: 9.5, height: 18 }} />
        )}
        {meta}
        {exploits.length > 0 && (
          <Tooltip title={`${exploits.length} known exploit${exploits.length === 1 ? "" : "s"}`}>
            <Chip
              icon={<BoltIcon sx={{ fontSize: "13px !important" }} />}
              label="Exploit available"
              size="small"
              sx={{ bgcolor: "#ff444422", color: "#ff4444", fontSize: 10, height: 18, fontWeight: 700, "& .MuiChip-icon": { color: "#ff4444" } }}
            />
          </Tooltip>
        )}
        {ackEnabled && ack && (
          <Tooltip title={`Acknowledged${ack.acknowledged_by ? ` by ${ack.acknowledged_by}` : ""}`}>
            <Chip
              icon={<CheckCircleIcon sx={{ fontSize: "13px !important" }} />}
              label="Acknowledged"
              size="small"
              sx={{ bgcolor: "#4cceac22", color: "#4cceac", fontSize: 10, height: 18, fontWeight: 700, "& .MuiChip-icon": { color: "#4cceac" } }}
            />
          </Tooltip>
        )}
        <ExpandMoreIcon
          sx={{
            ml: "auto", color: colors.grey[500], fontSize: 20, flexShrink: 0,
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 150ms",
          }}
        />
      </Box>

      {expanded && (
        <Box sx={{ p: 1.2, backgroundColor: "#1a1a1a" }}>
          {vuln.description && (
            <Box sx={{ mb: (exploits.length > 0 || ackEnabled) ? 1.2 : 0 }}>
              <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: colors.grey[500], textTransform: "uppercase", letterSpacing: 0.5, mb: 0.3 }}>
                Description
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: colors.grey[200], lineHeight: 1.5 }}>
                {vuln.description}
              </Typography>
            </Box>
          )}

          {exploits.length > 0 && (
            <Box sx={{ mb: ackEnabled ? 1.2 : 0 }}>
              <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "#ff4444", textTransform: "uppercase", letterSpacing: 0.5, mb: 0.3 }}>
                Known Exploits
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
                {exploits.map((ex, i) => (
                  <Box key={ex.exploit_id ?? i} sx={{ display: "flex", alignItems: "center", gap: 0.7, flexWrap: "wrap" }}>
                    <BoltIcon sx={{ color: "#ff4444", fontSize: 14, flexShrink: 0 }} />
                    {ex.source && (
                      <Chip label={ex.source} size="small" variant="outlined" sx={{ fontSize: 9.5, height: 17 }} />
                    )}
                    {ex.url ? (
                      <Link
                        href={ex.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        sx={{ fontSize: 12, color: "#7de8ed" }}
                      >
                        {ex.title || ex.exploit_id || ex.url}
                      </Link>
                    ) : (
                      <Typography sx={{ fontSize: 12, color: colors.grey[200] }}>
                        {ex.title || ex.exploit_id || "Untitled exploit"}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {ackEnabled && (
            <>
              <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: colors.grey[500], textTransform: "uppercase", letterSpacing: 0.5, mb: 0.5 }}>
                Remediation
              </Typography>
              <AckSection ack={ack} onAcknowledge={onAcknowledge} onReopen={onReopen} />
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

// Vertical accordion of vulnerabilities — one item expanded at a time. `origin`
// labels where a CVE is tied to (the firmware's `matched_on` field — a service,
// a software package, ...); pass a single `origin` string for a list-wide label,
// or pre-tag each vuln with `_origin` (as allVulnerabilitiesForAsset does) for a
// per-row label — `_origin` wins when present.
//
// Ack tracking (optional): pass `assetId` + `ackMap` (a Map keyed by vulnAckKey from
// assetUtils.js, built from the acks array by the caller) plus `onAcknowledge`/`onReopen`
// callbacks (asset_id, cve_id, originKey, note?) — when present, every row gets an
// acknowledge/reopen affordance. Omit all four and the list behaves exactly as before.
// A flat list spanning several assets (no single `assetId` in common) can tag each
// vuln with `_assetId` instead — same override convention as `_origin` above.
//
// `renderMeta(vuln)` (optional): extra content rendered in each row's header, after
// the origin chip — e.g. a link back to the asset this CVE belongs to.
// A "High" tier alone can run into the dozens on a poorly-patched asset —
// paged per tier (not one page for the whole list) so switching tiers
// doesn't lose your place in the one you were reading.
const VULNS_PAGE_SIZE = 8;

const VulnerabilityList = ({
  vulns,
  origin,
  emptyMessage = "No known vulnerabilities.",
  assetId,
  ackMap,
  onAcknowledge,
  onReopen,
  renderMeta,
  // Optional: renders every vuln in the open tier inside one fixed-height
  // scrollable list instead of paginated pages of VULNS_PAGE_SIZE. Pagination's
  // per-page row count still lets the card's height change between pages (a
  // last page of 1 row is much shorter than a full one), which shifts
  // whatever sits below it — a fixed height with internal scroll can't do
  // that, since the card's height stops depending on how many rows are on
  // screen. Omit it and every existing caller keeps the paginated behavior.
  maxHeight,
}) => {
  const sorted = useMemo(
    () => (vulns || []).slice().sort((a, b) => vulnSeverityRank(b) - vulnSeverityRank(a)),
    [vulns]
  );

  // Grouped by severity tier: a wall of 10+ same-looking rows reads as noise,
  // a handful of "Critical (11) / High (2)" sections doesn't — same tiers
  // used everywhere else severity is bucketed (assetUtils.js).
  const groups = useMemo(() => {
    const byTier = new Map();
    sorted.forEach((v) => {
      const tier = vulnSeverityTier(v);
      if (!byTier.has(tier)) byTier.set(tier, []);
      byTier.get(tier).push(v);
    });
    return SEVERITY_TIER_ORDER.filter((tier) => byTier.has(tier)).map((tier) => ({ tier, vulns: byTier.get(tier) }));
  }, [sorted]);

  const [expandedId, setExpandedId] = useState(null);
  // Only the worst-severity group starts open — the rest are one click away,
  // not dumped on screen at once.
  const [expandedTiers, setExpandedTiers] = useState(() => new Set(groups[0] ? [groups[0].tier] : []));
  const toggleTier = (tier) => setExpandedTiers((cur) => {
    const next = new Set(cur);
    if (next.has(tier)) next.delete(tier); else next.add(tier);
    return next;
  });

  // maxHeight mode only (Security tab): severity as a filter, not a grouping —
  // same interaction as ServicesSection's tag chips. Empty selection = show all.
  const [activeSeverityFilter, setActiveSeverityFilter] = useState([]);
  const toggleSeverityFilter = (tier) => setActiveSeverityFilter((cur) =>
    cur.includes(tier) ? cur.filter((t) => t !== tier) : [...cur, tier]
  );

  const [pageByTier, setPageByTier] = useState({});
  useEffect(() => setPageByTier({}), [sorted]);

  if (sorted.length === 0) {
    return <Typography sx={{ color: colors.grey[600], fontSize: 12.5, fontStyle: "italic" }}>{emptyMessage}</Typography>;
  }

  const ackEnabled = Boolean(ackMap && onAcknowledge && onReopen);

  const renderRow = (v, i) => {
    const originKey = v._originKey ?? null;
    const vulnAssetId = v._assetId ?? assetId;
    // A flat multi-asset list can have the same cve_id on more than one asset —
    // fold the asset id in so those don't collide as the same React key / accordion id.
    const id = `${vulnAssetId ?? ""}-${v.cve_id ?? i}`;
    const ack = ackEnabled ? ackMap.get(vulnAckKey(vulnAssetId, v.cve_id, originKey)) : undefined;
    return (
      <VulnerabilityRow
        key={id}
        vuln={v}
        origin={v._origin ?? origin}
        meta={renderMeta ? renderMeta(v) : null}
        expanded={expandedId === id}
        onToggle={() => setExpandedId((cur) => (cur === id ? null : id))}
        ack={ack}
        onAcknowledge={ackEnabled ? (note) => onAcknowledge(vulnAssetId, v.cve_id, originKey, note) : undefined}
        onReopen={ackEnabled ? () => onReopen(vulnAssetId, v.cve_id, originKey) : undefined}
      />
    );
  };

  const tierHeader = (tier, count, open, onClick) => {
    const style = severityStyleForTier(tier);
    const TierIcon = severityIconForTier(tier);
    return (
      <Box
        onClick={onClick}
        sx={{
          display: "flex", alignItems: "center", gap: 0.8,
          cursor: onClick ? "pointer" : "default",
          p: 0.8, borderRadius: 1, backgroundColor: style.bg,
          ...(onClick && { "&:hover": { filter: "brightness(1.15)" } }),
        }}
      >
        {TierIcon && <TierIcon sx={{ color: style.color, fontSize: 18, flexShrink: 0 }} />}
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: style.color, textTransform: "uppercase", letterSpacing: 0.4 }}>
          {style.label}
        </Typography>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: style.color, opacity: 0.8 }}>
          {count}
        </Typography>
        {onClick && (
          <ExpandMoreIcon
            sx={{
              ml: "auto", color: style.color, fontSize: 18, flexShrink: 0,
              transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 150ms",
            }}
          />
        )}
      </Box>
    );
  };

  // maxHeight mode (Security tab): every tier already expanded, no per-tier
  // pagination or collapse — one scrollable container for the whole list, set
  // to a fixed `height` (not `maxHeight`) so this card is exactly as tall as
  // Exposed Services even when there are too few CVEs to fill it — a
  // maxHeight-only box shrinks to fit its content, and the two cards would
  // only match by coincidence.
  if (maxHeight) {
    const filtered = activeSeverityFilter.length === 0
      ? sorted
      : sorted.filter((v) => activeSeverityFilter.includes(vulnSeverityTier(v)));
    return (
      <Box sx={{ display: "flex", flexDirection: "column", height: maxHeight }}>
        {groups.length > 1 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap", mb: 1, flexShrink: 0 }}>
            {groups.map(({ tier, vulns: tierVulns }) => {
              const style = severityStyleForTier(tier);
              const active = activeSeverityFilter.includes(tier);
              return (
                <Chip
                  key={tier}
                  label={`${style.label} (${tierVulns.length})`}
                  size="small"
                  onClick={() => toggleSeverityFilter(tier)}
                  sx={{
                    bgcolor: active ? style.color : style.bg,
                    color: active ? "#111" : style.color,
                    fontSize: 10.5, height: 20, fontWeight: 700, cursor: "pointer",
                    border: `1px solid ${active ? style.color : "transparent"}`,
                  }}
                />
              );
            })}
          </Box>
        )}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8, flex: 1, overflowY: "auto", pr: 0.5 }}>
          {filtered.map(renderRow)}
          {filtered.length === 0 && (
            <Typography sx={{ color: colors.grey[600], fontSize: 12.5, fontStyle: "italic" }}>
              No vulnerability matches the selected severity.
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
      {groups.map(({ tier, vulns: tierVulns }) => {
        const open = expandedTiers.has(tier);
        return (
          <Box key={tier}>
            {tierHeader(tier, tierVulns.length, open, () => toggleTier(tier))}

            {open && (() => {
              const pageCount = Math.max(1, Math.ceil(tierVulns.length / VULNS_PAGE_SIZE));
              const page = Math.min(pageByTier[tier] ?? 1, pageCount);
              const paged = tierVulns.slice((page - 1) * VULNS_PAGE_SIZE, page * VULNS_PAGE_SIZE);
              return (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8, mt: 0.8 }}>
                  {paged.map(renderRow)}
                  {pageCount > 1 && (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 0.4 }}>
                      <Pagination
                        count={pageCount}
                        page={page}
                        onChange={(_, p) => setPageByTier((cur) => ({ ...cur, [tier]: p }))}
                        size="small"
                        sx={{ "& .MuiPaginationItem-root": { color: colors.grey[300] } }}
                      />
                    </Box>
                  )}
                </Box>
              );
            })()}
          </Box>
        );
      })}
    </Box>
  );
};

export default VulnerabilityList;
